import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { ExternalLink, MoreHorizontal, Unlink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { getFacilitySecurityUnlinkPath } from "@/lib/facility-security-actions";
import { getSecurityDisplayName } from "@/lib/security-display-name";
import type { Security } from "@shared/schema";

interface LinkedSecurity extends Security {
  linkId: string;
  isFirstRanking: boolean | null;
  securityPosition: string | null;
  securedAmount: string | null;
  includeInLvr: boolean | null;
  lvrContributionPercentage: string | null;
  propertyAddress?: string | null;
  propertySuburb?: string | null;
  propertyState?: string | null;
  facilityCount?: number | null;
  vehicleMake?: string | null;
  vehicleModel?: string | null;
  vehicleRegistrationNumber?: string | null;
  equipmentMake?: string | null;
  equipmentModel?: string | null;
  equipmentSerialNumber?: string | null;
  equipmentAssetTag?: string | null;
  financialAssetDescription?: string | null;
  financialAssetType?: string | null;
  financialAssetInstitutionName?: string | null;
}

interface FacilitySecuritiesTableProps {
  facilityId: string;
  securities: LinkedSecurity[];
  isLoading: boolean;
  onRowClick: (securityId: string) => void;
  onViewInDealRegister: (securityId: string) => void;
}

const SECURITY_TYPE_LABELS: Record<string, string> = {
  residential_property: "Residential Property",
  commercial_property: "Commercial Property",
  vehicle: "Vehicle",
  equipment: "Equipment",
  financial_asset: "Financial Asset",
  guarantee: "Guarantee",
  general_security_agreement: "GSA",
};

const STATUS_COLORS: Record<string, string> = {
  identified: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  proposed: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  valued: "bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300",
  approved: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  registered: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900 dark:text-emerald-300",
  varied: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  substituted: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  discharged: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
  cancelled: "bg-gray-300 text-gray-600 dark:bg-gray-700 dark:text-gray-400",
};

export function FacilitySecuritiesTable({
  facilityId,
  securities,
  isLoading,
  onRowClick,
  onViewInDealRegister,
}: FacilitySecuritiesTableProps) {
  const { toast } = useToast();
  const [unlinkDialogOpen, setUnlinkDialogOpen] = useState(false);
  const [securityToUnlink, setSecurityToUnlink] = useState<string | null>(null);

  const unlinkMutation = useMutation({
    mutationFn: async (securityId: string) => {
      await apiRequest("DELETE", getFacilitySecurityUnlinkPath(facilityId, securityId));
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: [`/api/crm/facilities/${facilityId}/securities`],
      });
      void queryClient.invalidateQueries({
        queryKey: [`/api/crm/facilities/${facilityId}/securities/summary`],
      });
      toast({
        title: "Security unlinked",
        description: "The security has been removed from this facility.",
      });
      setUnlinkDialogOpen(false);
      setSecurityToUnlink(null);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to unlink security",
        variant: "destructive",
      });
      setUnlinkDialogOpen(false);
      setSecurityToUnlink(null);
    },
  });

  const handleUnlinkClick = (securityId: string) => {
    setSecurityToUnlink(securityId);
    setUnlinkDialogOpen(true);
  };

  const handleConfirmUnlink = () => {
    if (!securityToUnlink) return;
    unlinkMutation.mutate(securityToUnlink);
  };

  // Format currency
  const formatCurrency = (value: string | null | undefined): string => {
    if (!value) return "-";
    const num = parseFloat(value);
    if (isNaN(num)) return "-";
    return new Intl.NumberFormat("en-AU", {
      style: "currency",
      currency: "AUD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Get short security reference
  const getSecurityRef = (security: LinkedSecurity): string => {
    // Generate reference from type + id
    const typeAbbr = security.securityType?.slice(0, 3).toUpperCase() || "SEC";
    const idShort = security.id.slice(0, 6);
    return `${typeAbbr}-${idShort}`;
  };

  // Get security description
  const getDescription = (security: LinkedSecurity): string => {
    return getSecurityDisplayName(security);
  };

  // Get property address (for property security types)
  const getPropertyAddress = (security: LinkedSecurity): string | null => {
    const isPropertyType = security.securityType === 'residential_property' ||
                           security.securityType === 'commercial_property';

    if (!isPropertyType) return null;

    if (security.propertyAddress) {
      return security.propertyAddress;
    }

    // Fallback: construct from individual fields
    if (security.propertySuburb || security.propertyState) {
      return [security.propertySuburb, security.propertyState]
        .filter(Boolean)
        .join(', ');
    }

    return null;
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-3">
          <p className="text-sm text-muted-foreground text-center py-8">
            Loading securities...
          </p>
        </CardContent>
      </Card>
    );
  }

  if (securities.length === 0) {
    return (
      <Card>
        <CardContent className="pt-3">
          <p className="text-sm text-muted-foreground text-center py-8">
            No securities from the central deal register are linked to this facility yet.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <Table>
          <TableHeader>
            <TableRow className="h-8 bg-muted/50">
              <TableHead className="px-2 py-1 text-xs font-medium w-[100px]">
                Ref
              </TableHead>
              <TableHead className="px-2 py-1 text-xs font-medium w-[90px]">
                Type
              </TableHead>
              <TableHead className="px-2 py-1 text-xs font-medium">
                Description
              </TableHead>
              <TableHead className="px-2 py-1 text-xs font-medium w-[180px]">
                Address
              </TableHead>
              <TableHead className="px-2 py-1 text-xs font-medium text-right w-[100px]">
                Value
              </TableHead>
              <TableHead className="px-2 py-1 text-xs font-medium text-right w-[60px]">
                LVR%
              </TableHead>
              <TableHead className="px-2 py-1 text-xs font-medium w-[80px]">
                Status
              </TableHead>
              <TableHead className="px-2 py-1 text-xs font-medium w-[70px]">
                Actions
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {securities.map((security) => (
              <TableRow
                key={security.id}
                className="h-8 cursor-pointer hover:bg-muted/50"
                onClick={() => onRowClick(security.id)}
                data-testid={`security-row-${security.id}`}
              >
                <TableCell className="px-2 py-1">
                  <span className="text-xs font-mono text-primary">
                    {getSecurityRef(security)}
                  </span>
                </TableCell>
                <TableCell className="px-2 py-1">
                  <Badge variant="outline" className="text-xs">
                    {SECURITY_TYPE_LABELS[security.securityType] || security.securityType}
                  </Badge>
                </TableCell>
                <TableCell className="px-2 py-1 text-sm max-w-[200px]">
                  <div className="flex flex-col gap-0.5">
                    <span className="truncate">{getDescription(security)}</span>
                    {security.facilityCount != null && security.facilityCount > 1 && (
                      <Badge
                        variant="outline"
                        className="text-xs w-fit bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950 dark:text-blue-300 dark:border-blue-800"
                        data-testid="cross-collateralised-badge"
                      >
                        Cross-collateralised ({security.facilityCount} facilities)
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="px-2 py-1 text-sm truncate max-w-[180px]">
                  {getPropertyAddress(security) || "-"}
                </TableCell>
                <TableCell className="px-2 py-1 text-sm text-right font-mono">
                  {formatCurrency(security.estimatedValue)}
                </TableCell>
                <TableCell className="px-2 py-1 text-sm text-right font-mono">
                  {security.lvrContributionPercentage
                    ? `${security.lvrContributionPercentage}%`
                    : "100%"}
                </TableCell>
                <TableCell className="px-2 py-1">
                  <Badge
                    variant="secondary"
                    className={`text-xs capitalize ${
                      STATUS_COLORS[security.status ?? "identified"] || ""
                    }`}
                  >
                    {(security.status ?? "identified").replace("_", " ")}
                  </Badge>
                </TableCell>
                <TableCell className="px-2 py-1">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                      <Button variant="ghost" size="icon" className="h-6 w-6" data-testid="security-actions-button">
                        <MoreHorizontal className="h-3 w-3" />
                      </Button>
                    </DropdownMenuTrigger>
                    {/* The menu is portalled, but React still sends its clicks up to the row's
                        onClick. Stop them here so Unlink opens its dialog (issue 2241). */}
                    <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem onClick={() => onViewInDealRegister(security.id)}>
                        <ExternalLink className="h-3 w-3 mr-2" />
                        Open in Deal Register
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:text-destructive"
                        onClick={() => handleUnlinkClick(security.id)}
                        disabled={unlinkMutation.isPending}
                        data-testid="unlink-security-button"
                      >
                        <Unlink className="h-3 w-3 mr-2" />
                        Unlink
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Unlink Confirmation Dialog */}
      <AlertDialog open={unlinkDialogOpen} onOpenChange={setUnlinkDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unlink Security</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to unlink this security from the facility?
              This action will remove the link but will not delete the security itself.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmUnlink}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-unlink-button"
            >
              Confirm
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
